import { useState, useEffect, useRef } from "react";
import { ENDPOINT } from "../../../constants/endpointConstants";
import axios from "axios";
import { getToken, formatDateTime } from "../../../utils/utils";
import { Toast, ToastContainer } from "react-bootstrap";
import ErrorModal from "../../../components/ErrorModal";
import KitchenPrint from "../../../components/KitchenPrint";
import { useReactToPrint } from "react-to-print";

const CashierForm = ({
  buttonAction,
  saleToUpdate,
  productsToUpdate,
  deliveryPriceToUpdate,
}) => {
  const kitchenRef = useRef();

  const defaultProducts = [
    {
      productId: "",
      productName: "",
      productPrice: 0,
      amount: 1,
      total: 0,
      changes: "",
      sauces: "",
      deliveryPrice: 0,
    },
  ];

  const [currentProducts, setCurrentProducts] = useState([]);
  const [products, setProducts] = useState(
    productsToUpdate ? productsToUpdate : defaultProducts
  );
  const [totalSale, setTotalSale] = useState(
    saleToUpdate?.totalPrice ? saleToUpdate.totalPrice : 0
  );
  const [orderIdForPrint, setOrderIdForPrint] = useState(null);
  const [userName, setUserName] = useState(
    saleToUpdate?.userName ? saleToUpdate.userName : ""
  );
  const [isLoading, setIsLoading] = useState(false);
  let isFormDisabled = false;
  const [showDetails, setShowDetails] = useState(
    Array(products.length).fill(false)
  );
  const [showDeliveryPrice, setShowDeliveryPrice] = useState(
    deliveryPriceToUpdate ? true : false
  );
  const [deliveryPrice, setDeliveryPrice] = useState(
    deliveryPriceToUpdate ? deliveryPriceToUpdate : 0
  );
  const [deliveryPriceError, setDeliveryPriceError] = useState(false);
  const [toasts, setToasts] = useState([]);

  const showToast = (message, variant = "danger") => {
    const id = new Date().getTime() + Math.random();
    setToasts((prevToasts) => [...prevToasts, { id, message, variant }]);
  };

  const showSupplyNotifications = (saleResponse) => {
    if (saleResponse && Array.isArray(saleResponse.supplyNotifications)) {
      saleResponse.supplyNotifications.forEach((supply) => {
        showToast(
          <span>
            ¡Se acaba! <strong>{supply.name}</strong> tiene menos de:{" "}
            <strong>{supply.notificationLimit}</strong>
          </span>
        );
      });
    }
  };

  const handlePrint = useReactToPrint({
    content: () => kitchenRef.current,
  });

  useEffect(() => {
    getProducts();
  }, []);

  useEffect(() => {
    const calculateTotalSale = () => {
      let sumSale = 0;
      products.forEach((product) => {
        sumSale += product.total;
      });
      setTotalSale(sumSale + deliveryPrice);
    };

    calculateTotalSale();
  }, [products, deliveryPrice]);

  useEffect(() => {
    if (orderIdForPrint) {
      handlePrint();
      setOrderIdForPrint(null);
    }
  }, [orderIdForPrint, handlePrint]);

  const getProducts = async () => {
    const token = getToken();
    const response = await axios.get(ENDPOINT.GET_PRODUCTS, token);
    setCurrentProducts(response.data);
  };

  const addNewProduct = () => {
    setProducts([
      ...products,
      {
        productId: "",
        productName: "",
        productPrice: 0,
        total: 0,
        changes: "",
        sauces: "",
      },
    ]);
    setShowDetails([...showDetails, false]);
  };

  const handleToggleDeliveryPrice = () =>
    setShowDeliveryPrice((prev) => {
      if (prev) {
        setDeliveryPrice(0);
        setDeliveryPriceError(false);
      }
      return !prev;
    });

  const deliveryPriceOnChange = (e) => {
    let value = e.target.value;
    if (value === "") value = "";
    else value = Number(value);
    setDeliveryPrice(value);
    if (value === "" || isNaN(value) || value < 0 || value > 30000) {
      setDeliveryPriceError(true);
    } else {
      setDeliveryPriceError(false);
    }
  };

  const changesOnChange = ($event, index) => {
    let newProducts = [...products];
    newProducts[index] = {
      ...newProducts[index],
      changes: $event.target.value,
    };
    setProducts(newProducts);
  };

  const saucesOnChange = ($event, index) => {
    let newProducts = [...products];
    newProducts[index] = {
      ...newProducts[index],
      sauces: $event.target.value,
    };
    setProducts(newProducts);
  };

  const productOnChange = ($event, index) => {
    const productId = parseInt($event.target.value);
    let newProducts = [...products];
    const productSelected = currentProducts.find(
      (product) => product.productId === productId
    );
    newProducts[index] = {
      productId,
      productName: productSelected.name,
      productPrice: productSelected.salePrice,
      changes: "",
      amount: 1,
      total: productSelected.salePrice,
      sauces: "",
    };
    setProducts(newProducts);
  };

  const removeProduct = (index) => {
    let newProducts = [...products];
    newProducts.splice(index, 1);
    setProducts(newProducts);
    const newShowDetails = [...showDetails];
    newShowDetails.splice(index, 1);
    setShowDetails(newShowDetails);
  };

  const sendProducts = async () => {
    const payload = {
      date: formatDateTime(new Date()),
      clientName: userName ? userName : "Cliente",
      saleProducts: products,
      deliveryPrice: deliveryPrice,
      status: 0,
    };
    try {
      const token = getToken();
      setIsLoading(true);
      const response = await axios.post(ENDPOINT.NEW_SALE, payload, token);
      showSupplyNotifications(response.data);
      if (response.data && response.data.dailyOrderNumber) {
        setOrderIdForPrint(response.data.dailyOrderNumber);
      } else {
        console.warn(
          "Daily Order Number not found in response for sendProducts"
        );
      }
      setIsLoading(false);
      buttonAction(response.data);
      setProducts(defaultProducts);
      setUserName("");
      setTotalSale(0);
      setShowDetails(Array(defaultProducts.length).fill(false));
      setDeliveryPrice(0);
      setShowDeliveryPrice(false);
    } catch (error) {
      setIsLoading(false);
      setOrderIdForPrint(null);
      console.error("Error sending products:", error);
    }
  };

  const updateSale = async () => {
    const payload = {
      saleId: saleToUpdate.saleId,
      clientName: userName ? userName : "Cliente",
      saleProducts: products,
      deliveryPrice: deliveryPrice,
      date: saleToUpdate.date,
    };
    try {
      const token = getToken();
      setIsLoading(true);
      const response = await axios.put(ENDPOINT.UPDATE_SALE, payload, token);
      showSupplyNotifications(response.data);
      if (response.data && response.data.dailyOrderNumber) {
        setOrderIdForPrint(response.data.dailyOrderNumber);
      } else {
        console.warn(
          "Daily Order Number not found in response for updateSale, using saleToUpdate.saleId"
        );
        setOrderIdForPrint(saleToUpdate.saleId);
      }
      setIsLoading(false);
      buttonAction(response.data);
    } catch (error) {
      setIsLoading(false);
      setOrderIdForPrint(null);
      console.error("Error updating sale:", error);
    }
  };

  const nameOnChange = (event) => {
    setUserName(event.target.value);
  };

  const isButtonDisabled = () =>
    isFormDisabled ||
    products.some((product) => !product.productId || !product.amount) ||
    deliveryPrice === "" ||
    isNaN(deliveryPrice) ||
    deliveryPrice < 0 ||
    deliveryPrice > 30000;

  const isRemoveButtonDisabled = () => products.length && products.length - 1;

  const buttonContent = () => {
    if (isLoading) {
      return (
        <button type="button" className="btn btn-table btn-sm px-2 py-1">
          <span
            className="spinner-border spinner-border-sm"
            role="status"
            aria-hidden="true"
          ></span>
        </button>
      );
    } else if (saleToUpdate) {
      return (
        <button
          type="button"
          className="btn btn-table btn-sm px-2 py-1"
          onClick={updateSale}
          disabled={isButtonDisabled()}
        >
          ACTUALIZAR
        </button>
      );
    } else {
      return (
        <button
          type="button"
          className="btn btn-table btn-sm px-2 py-1"
          onClick={sendProducts}
          disabled={isButtonDisabled()}
        >
          AGREGAR
        </button>
      );
    }
  };

  const toggleDetails = (index) => {
    const newShowDetails = [...showDetails];
    newShowDetails[index] = !newShowDetails[index];
    setShowDetails(newShowDetails);
  };

  return (
    <div className="cashier-form-vertical rounded p-3 bg-light">
      <ToastContainer
        position="top-center"
        className="toas-container p-3 position-fixed top-0 start-0 end-0"
      >
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            onClose={() =>
              setToasts((prev) => prev.filter((t) => t.id !== toast.id))
            }
            bg={toast.variant}
            autohide
            delay={10000}
            className="mb-2"
          >
            <Toast.Header closeButton>
              <strong className="me-auto">¡Atención!</strong>
            </Toast.Header>
            <Toast.Body className="text-white">{toast.message}</Toast.Body>
          </Toast>
        ))}
      </ToastContainer>
      <div className="mb-3">
        <label htmlFor="clientName" className="form-label">
          Cliente
        </label>
        <input
          type="text"
          className="form-control"
          value={userName}
          onChange={nameOnChange}
        />
      </div>

      {products.map((product, index) => (
        <div className="product-item-vertical mb-3" key={index}>
          <div className="mb-2">
            <select
              className={`form-select mb-2 ${
                !product.productId ? "attention-needed" : ""
              }`}
              value={product.productId}
              onChange={(e) => productOnChange(e, index)}
            >
              <option value="">Seleccione un producto</option>
              {currentProducts.map((product) => (
                <option key={product.productId} value={product.productId}>
                  {product.name}
                </option>
              ))}
            </select>

            <div className="text-center">
              <button
                className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                onClick={() => toggleDetails(index)}
                aria-expanded={showDetails[index]}
              >
                <i
                  className={`fas fa-${showDetails[index] ? "minus" : "plus"} me-2`}
                ></i>
                {showDetails[index] ? "Detalles" : "Detalles"}
              </button>
            </div>
          </div>

          {showDetails[index] && (
            <div className="accordion-body">
              <div className="row g-2 mb-2">
                <div className="col-md-6">
                  <label htmlFor={`changes-${index}`} className="form-label">
                    Cambios
                  </label>
                  <input
                    id={`changes-${index}`}
                    type="text"
                    className="form-control"
                    value={product.changes}
                    onChange={(e) => changesOnChange(e, index)}
                  />
                </div>
                <div className="col-md-6">
                  <label htmlFor={`sauces-${index}`} className="form-label">
                    Salsas
                  </label>
                  <input
                    id={`sauces-${index}`}
                    type="text"
                    className="form-control"
                    value={product.sauces || ""}
                    onChange={(e) => saucesOnChange(e, index)}
                  />
                </div>
              </div>
            </div>
          )}
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <span className="fw-bold">
                {"$" + new Intl.NumberFormat("es-CL").format(product.total)}
              </span>
            </div>
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={() => removeProduct(index)}
              disabled={!isRemoveButtonDisabled()}
            >
              <i className="fa-solid fa-trash"></i>
            </button>
          </div>
        </div>
      ))}

      <button
        type="button"
        className="btn btn-primary btn-sm"
        onClick={addNewProduct}
      >
        <i className="fa-solid fa-plus"></i>
      </button>

      <div className="d-flex justify-content-between align-items-center mt-4 mb-5">
        <div className="col-4">
          <div className="form-check form-switch mb-2">
            <label className="form-check-label" htmlFor="toggleDeliveryPrice">
              Domicilio?
            </label>
            <input
              className="form-check-input"
              type="checkbox"
              id="toggleDeliveryPrice"
              checked={showDeliveryPrice}
              onChange={handleToggleDeliveryPrice}
            />
          </div>
          {showDeliveryPrice && (
            <>
              <input
                id="deliveryPrice"
                type="number"
                placeholder="Valor domicilio"
                min={0}
                max={30000}
                className={`form-control ${deliveryPriceError ? "is-invalid" : ""}`}
                value={deliveryPrice === 0 ? "" : deliveryPrice}
                onChange={deliveryPriceOnChange}
              />
              {deliveryPriceError && (
                <div className="invalid-feedback">
                  El precio debe ser entre 1 y 30.000
                </div>
              )}
            </>
          )}
        </div>

        <div className="text-end">
          <p className="mb-1 fw-bold">Total Orden</p>
          <p className="fs-5 mb-0">
            {"$" + new Intl.NumberFormat("es-CL").format(totalSale)}
          </p>
        </div>
      </div>

      <div className="text-center">{buttonContent()}</div>

      <ErrorModal></ErrorModal>
      <div className="d-none">
        <KitchenPrint
          ref={kitchenRef}
          products={products}
          userName={userName}
          orderId={orderIdForPrint}
        />
      </div>
    </div>
  );
};

export default CashierForm;
